/**
 * src/components/shared/CommandMenu.jsx
 * Global command palette — open with Cmd+K (Mac) / Ctrl+K (Windows/Linux).
 * Uses the `cmdk` library for accessible, fuzzy-searchable commands.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import { canAdmin } from '../../utils/auth.js';
import { useControlFrameworkPreference } from '../../state/ControlFrameworkPreferenceContext.jsx';
import { resolveControlDisplayLabel } from '../../utils/controlFrameworkPreference.js';

// ── Icons (geometric, matching sidebar style) ──────────────────────────────
const NAV_ITEMS = [
  { id: 'nav-dashboard', label: 'Dashboard', icon: '⬡', path: '/dashboard', hint: null },
  { id: 'nav-documents', label: 'Documents', icon: '◈', path: '/documents', hint: null },
  { id: 'nav-tasks', label: 'Tasks', icon: '◫', path: '/tasks', hint: null },
  { id: 'nav-controls', label: 'Controls', icon: '◉', path: '/controls', hint: null },
  { id: 'nav-frameworks', label: 'Framework Manager', icon: '⬢', path: '/frameworks', hint: null },
  { id: 'nav-risks', label: 'Risks & Opportunities', icon: '△', path: '/risks', hint: null },
  { id: 'nav-threats', label: 'Threats', icon: '⚡', path: '/threats', hint: null },
  { id: 'nav-vendors', label: 'Vendors', icon: '◈', path: '/vendors', hint: null },
  { id: 'nav-gap', label: 'Gap Analysis', icon: '◑', path: '/gap', hint: null },
  { id: 'nav-settings', label: 'Settings', icon: '⚙', path: '/settings', hint: null },
];

const ADMIN_NAV_ITEMS = [
  { id: 'nav-users', label: 'User Management', icon: '◐', path: '/users', hint: null },
  { id: 'nav-log', label: 'Activity Log', icon: '◎', path: '/log', hint: null },
  { id: 'nav-secevt', label: 'Security Events', icon: '◉', path: '/security-events', hint: null },
];

export default function CommandMenu({ state, session, pushModal, allControls }) {
  const { preferredFrameworkId, enabledFrameworkIds } = useControlFrameworkPreference();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const isAdmin = canAdmin(session?.role);

  // ── Toggle on Ctrl+K / Cmd+K ──────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ── Reset search when closed ──────────────────────────────────────────────
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const run = useCallback((fn) => {
    setOpen(false);
    // Small delay so the palette animates out before navigation
    setTimeout(fn, 60);
  }, []);

  const goTo = useCallback((path) => run(() => navigate(path)), [run, navigate]);

  // ── Document items from state ─────────────────────────────────────────────
  const documents = state?.documents || [];

  // ── Controls (ISO 27001 + CIS18) ─────────────────────────────────────────
  const controls = Array.isArray(allControls) ? allControls : [];

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '12vh',
        background: 'rgba(10,9,8,0.75)',
        backdropFilter: 'blur(4px)',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <Command
        ref={inputRef}
        className="command-palette"
        style={{
          width: '100%',
          maxWidth: '580px',
          background: 'var(--bg2)',
          border: '1px solid var(--border2)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
          overflow: 'hidden',
          fontFamily: 'var(--font-body)',
          color: 'var(--fg)',
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        value={search}
        onValueChange={setSearch}
        loop
      >
        {/* ── Search input ─────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span style={{ fontSize: 16, color: 'var(--fg3)', flexShrink: 0 }}>⌕</span>
          <Command.Input
            autoFocus
            placeholder="Search commands, controls, documents…"
            style={{
              flex: 1,
              background: 'none',
              border: 'none',
              outline: 'none',
              fontSize: 15,
              color: 'var(--fg)',
              fontFamily: 'var(--font-body)',
              caretColor: 'var(--accent)',
            }}
          />
          <kbd
            style={{
              padding: '2px 7px',
              background: 'var(--bg4)',
              border: '1px solid var(--border2)',
              borderRadius: 5,
              fontSize: 11,
              color: 'var(--fg3)',
              fontFamily: 'var(--font-mono)',
              flexShrink: 0,
            }}
          >
            Esc
          </kbd>
        </div>

        {/* ── Results list ─────────────────────────────────────────── */}
        <Command.List
          style={{
            maxHeight: '420px',
            overflowY: 'auto',
            padding: '8px',
          }}
        >
          <Command.Empty
            style={{
              padding: '28px 0',
              textAlign: 'center',
              color: 'var(--fg3)',
              fontSize: 13,
            }}
          >
            No results for &ldquo;{search}&rdquo;
          </Command.Empty>

          {/* ── Navigation ─────────────────────────────────────────── */}
          <CmdGroup heading="Navigation">
            {NAV_ITEMS.map((item) => (
              <CmdItem
                key={item.id}
                keywords={[item.label]}
                onSelect={() => goTo(item.path)}
                icon={item.icon}
                label={item.label}
                hint={item.hint}
              />
            ))}
            {isAdmin &&
              ADMIN_NAV_ITEMS.map((item) => (
                <CmdItem
                  key={item.id}
                  keywords={[item.label]}
                  onSelect={() => goTo(item.path)}
                  icon={item.icon}
                  label={item.label}
                  hint={item.hint}
                />
              ))}
          </CmdGroup>

          {/* ── Actions ────────────────────────────────────────────── */}
          <CmdGroup heading="Actions">
            <CmdItem
              keywords={['create', 'document', 'new']}
              onSelect={() => run(() => pushModal({ type: 'document-form', data: null }))}
              icon="+"
              label="New document"
            />
            <CmdItem
              keywords={['create', 'task', 'new']}
              onSelect={() => run(() => pushModal({ type: 'task-form', data: null }))}
              icon="+"
              label="New task"
            />
            <CmdItem
              keywords={['action', 'centre', 'approvals', 'pending']}
              onSelect={() => run(() => pushModal({ type: 'action-centre' }))}
              icon="◎"
              label="Open Action Centre"
            />
            <CmdItem
              keywords={['navigate', 'controls', 'jump', 'go']}
              onSelect={() => goTo('/controls')}
              icon="◉"
              label="Go to Controls"
            />
          </CmdGroup>

          {/* ── Documents ──────────────────────────────────────────── */}
          {documents.length > 0 && (
            <CmdGroup heading="Documents">
              {documents.map((doc) => (
                <CmdItem
                  key={doc.id}
                  keywords={[doc.title || '', doc.type || '']}
                  onSelect={() => run(() => pushModal({ type: 'document-detail', data: doc }))}
                  icon="◈"
                  label={doc.title || 'Untitled document'}
                  meta={doc.type}
                />
              ))}
            </CmdGroup>
          )}

          {/* ── Controls (ISO 27001 + CIS18) ───────────────────────── */}
          {controls.length > 0 && (
            <CmdGroup heading="Controls">
              {controls.map((ctrl) => (
                <CmdItem
                  key={ctrl.id}
                  keywords={[ctrl.id, ctrl.title, ctrl.section || '']}
                  onSelect={() => run(() => pushModal({ type: 'control-detail', control: ctrl }))}
                  icon="◉"
                  label={ctrl.title}
                  meta={resolveControlDisplayLabel(
                    ctrl.id,
                    preferredFrameworkId,
                    enabledFrameworkIds
                  )}
                />
              ))}
            </CmdGroup>
          )}

          {/* ── Users (admin only) ────────────────────────────────── */}
          {isAdmin && (
            <CmdGroup heading="Users">
              <CmdItem
                keywords={['users', 'manage', 'team', 'people']}
                onSelect={() => goTo('/users')}
                icon="◐"
                label="Manage users"
              />
              <CmdItem
                keywords={['invite', 'user', 'add', 'new']}
                onSelect={() => goTo('/users')}
                icon="+"
                label="Invite user"
              />
            </CmdGroup>
          )}

          {/* ── Service Lab (admin only, hidden) ─────────────────── */}
          {isAdmin && (
            <CmdGroup heading="Service Lab">
              <CmdItem
                keywords={[
                  'service',
                  'lab',
                  'consulting',
                  'project',
                  'bcp',
                  'business continuity',
                  'contingency',
                  'delivery',
                ]}
                onSelect={() => goTo('/service-lab')}
                icon="⚗"
                label="BCP & Contingency — Project Plan"
                meta="lab"
              />
            </CmdGroup>
          )}
        </Command.List>

        {/* ── Footer hint bar ───────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            padding: '8px 16px',
            borderTop: '1px solid var(--border)',
            fontSize: 11,
            color: 'var(--fg3)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <span>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> navigate
          </span>
          <span>
            <Kbd>↵</Kbd> select
          </span>
          <span>
            <Kbd>Esc</Kbd> close
          </span>
        </div>
      </Command>

      <style>{`
        [cmdk-item] {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 10px;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
          color: var(--fg2);
          transition: background 0.1s;
          outline: none;
          user-select: none;
        }
        [cmdk-item][data-selected='true'] {
          background: var(--accent-bg);
          color: var(--fg);
        }
        [cmdk-item][data-selected='true'] .cmd-icon {
          color: var(--accent);
        }
        [cmdk-group-heading] {
          padding: 6px 10px 4px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--fg3);
          font-family: var(--font-mono);
        }
        [cmdk-group]:not(:first-child) {
          margin-top: 4px;
          padding-top: 4px;
          border-top: 1px solid var(--border);
        }
      `}</style>
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

function CmdGroup({ heading, children }) {
  return <Command.Group heading={heading}>{children}</Command.Group>;
}

function CmdItem({ keywords, onSelect, icon, label, hint, meta }) {
  return (
    <Command.Item keywords={keywords} onSelect={onSelect}>
      <span
        className="cmd-icon"
        style={{
          width: 20,
          textAlign: 'center',
          fontSize: 14,
          flexShrink: 0,
          color: 'var(--fg3)',
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      {meta && (
        <span
          style={{
            fontSize: 11,
            color: 'var(--fg3)',
            fontFamily: 'var(--font-mono)',
            flexShrink: 0,
            marginLeft: 8,
          }}
        >
          {meta}
        </span>
      )}
      {hint && <Kbd>{hint}</Kbd>}
    </Command.Item>
  );
}

function Kbd({ children }) {
  return (
    <kbd
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2px 6px',
        background: 'var(--bg4)',
        border: '1px solid var(--border2)',
        borderRadius: 4,
        fontSize: 10,
        color: 'var(--fg3)',
        fontFamily: 'var(--font-mono)',
      }}
    >
      {children}
    </kbd>
  );
}
